import { useEffect, useRef, useState } from 'react'
import { Badge, Box, Button, Card, Flex, HStack, Spinner, Stack, Text } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { LuCalendarOff, LuCalendarPlus, LuClock } from 'react-icons/lu'
import { type AgendaEvent, getAgendaEventsService } from '@/services/events'
import { capitalizeFirstLetter } from '@/utils/string'
import { EVENT_STATUS_CONFIG } from '@/utils/events'
import CreateEventDialog from './CreateEventDialog'

const formatTime = (time: string): string => {
  const parts = time.split(':')
  const h = parseInt(parts[0])
  const m = parts[1] ?? '00'
  const period = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 || 12
  return `${h12}:${m} ${period}`
}

const TODAY_KEY = format(new Date(), 'yyyy-MM')

const findTargetKey = (groups: ReturnType<typeof groupEventsByMonth>): string | undefined => {
  const datedGroups = groups.filter(g => g.key !== '__sem-data__')
  return (
    [...datedGroups].reverse().find(g => g.key >= TODAY_KEY)?.key ??
    datedGroups[0]?.key ??
    groups[0]?.key
  )
}

const scrollToTarget = (container: HTMLElement, target: HTMLElement) => {
  container.scrollTop =
    target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop
}

const groupEventsByMonth = (events: AgendaEvent[]) => {
  const groups = new Map<string, AgendaEvent[]>()

  for (const event of events) {
    const key = event.event_date
      ? event.event_date.slice(0, 7) // "YYYY-MM"
      : '__sem-data__'
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(event)
  }

  return Array.from(groups.entries()).map(([key, groupEvents]) => ({
    key,
    dateLabel:
      key === '__sem-data__'
        ? 'Sem data'
        : format(new Date(key + '-01T00:00:00'), 'MMMM yyyy', { locale: ptBR }).toUpperCase(),
    events: groupEvents,
  }))
}

const EventsTab = () => {
  const { agendaId } = useParams<{ agendaId: string }>()
  const navigate = useNavigate()
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const targetRef = useRef<HTMLDivElement>(null)

  const { data: events, isLoading } = useQuery({
    queryKey: ['agendas', agendaId, 'events'],
    queryFn: () => getAgendaEventsService(agendaId!),
  })

  const groups = events && events.length > 0 ? groupEventsByMonth(events) : []
  const targetKey = findTargetKey(groups)

  useEffect(() => {
    const container = scrollContainerRef.current
    const target = targetRef.current
    if (!container || !target) return
    scrollToTarget(container, target)
  }, [events])

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <Spinner size="lg" color="primary.500" />
      </Box>
    )
  }

  if (!events || events.length === 0) {
    return (
      <>
        <Box display="flex" flexDir="column" alignItems="center" gap={3} py={10} color="fg.muted">
          <LuCalendarOff size={32} />
          <Text fontSize="sm">Nenhum evento encontrado.</Text>
          <Button size="sm" colorPalette="blue" onClick={() => setIsCreateOpen(true)}>
            <LuCalendarPlus />
            Criar evento
          </Button>
        </Box>
        {isCreateOpen && <CreateEventDialog open onOpenChange={setIsCreateOpen} />}
      </>
    )
  }

  return (
    <Box position="relative">
      <Stack
        ref={scrollContainerRef}
        gap={5}
        px={4}
        mt={4}
        pb={16}
        maxH={{ base: 'calc(100dvh - 320px)', md: 'calc(100vh - 220px)' }}
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'var(--chakra-colors-border-muted)', borderRadius: '999px' },
          '&::-webkit-scrollbar-thumb:hover': { background: 'var(--chakra-colors-border-emphasized)' },
          'scrollbarWidth': 'thin',
          'scrollbarColor': 'var(--chakra-colors-border-muted) transparent',
        }}
      >
        {groups.map(({ key, dateLabel, events: groupEvents }) => (
          <Box key={dateLabel} ref={key === targetKey ? targetRef : undefined}>
            <Text
              position="sticky"
              top={0}
              zIndex={1}
              bg="bg"
              fontSize="md"
              fontWeight="semibold"
              color="fg.muted"
              letterSpacing="widest"
              py={2}
              mb={1}
            >
              {dateLabel}
            </Text>
            <Stack gap={2}>
              {groupEvents.map((event) => {
                const status = EVENT_STATUS_CONFIG[event.status] ?? { label: event.status, colorPalette: 'gray' }
                return (
                  <Card.Root
                    key={event.id}
                    variant="outline"
                    w="full"
                    cursor="pointer"
                    _hover={{ bg: 'bg.subtle' }}
                    transition="background 0.15s"
                    onClick={() => navigate(`/painel/agendas/${agendaId}/evento/${event.id}`)}
                  >
                    <Card.Body py={3} px={4}>
                      <Flex align="center" justify="space-between" gap={3}>
                        <HStack gap={3} flex="1" minW="0">
                          <Box
                            bg={`${status.colorPalette}.subtle`}
                            color={`${status.colorPalette}.600`}
                            px={2}
                            py={1}
                            borderRadius="md"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            flexShrink={0}
                            minW="42px"
                          >
                            <Text fontWeight="bold" fontSize="lg" lineHeight="1">
                              {event.event_date ? format(new Date(event.event_date + 'T00:00:00'), 'dd') : '—'}
                            </Text>
                            <Text fontSize="xs" lineHeight="1" mt={0.5} textTransform="uppercase" fontWeight={700}>
                              {event.event_date ? capitalizeFirstLetter(format(new Date(event.event_date + 'T00:00:00'), 'EEE', { locale: ptBR })).slice(0, 3) : ''}
                            </Text>
                          </Box>
                          <Box minW="0">
                            <Text fontWeight="semibold" fontSize={{ base: 'xs', md: 'sm' }}>
                              {event.name}
                            </Text>
                            {event.event_time && (
                              <HStack gap={1} mt={0.5}>
                                <LuClock size={11} color="var(--chakra-colors-fg-muted)" />
                                <Text fontSize="sm" color="fg.muted">
                                  {formatTime(event.event_time)}
                                </Text>
                              </HStack>
                            )}
                          </Box>
                        </HStack>
                        <Badge colorPalette={status.colorPalette} flexShrink={0} size="sm">
                          {status.label}
                        </Badge>
                      </Flex>
                    </Card.Body>
                  </Card.Root>
                )
              })}
            </Stack>
          </Box>
        ))}
      </Stack>
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        h="60px"
        pointerEvents="none"
        bgGradient="to-b"
        gradientFrom="transparent"
        gradientTo="bg"
      />
    </Box>
  )
}

export default EventsTab
